import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useCallback, useMemo, useState } from 'react'
import { generateApiErrorPayload } from 'api-client/helpers'
import { ApiResponseErrorModel } from 'api-client/model'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import Tooltip from 'core/elements/tooltip'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { updateHostRole, updateResmgrHost } from '../actions'

export enum VMHAStatusForHosts {
  ACTIVE = 'Active',
  FAILED = 'Failed',
  CONFIGURING = 'Configuring',
  DISABLED = 'Disabled',
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  retryIcon: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: '24px',
    padding: theme.spacing(0, 0.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(0.5),
  },
  retryIconDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  container: {
    display: 'flex',
    gap: theme.spacing(0.25),
    alignItems: 'baseline',
  },
}))

/**
 * VMHAStatusCell component displays the VM HA status for a host
 * Also shows a retry button when the status is Failed to reapply the HA slave role
 * @param item - The host item containing vmhaStatus and other properties
 * @returns
 */
export default function VMHAStatusCell({ item }) {
  const classes = useStyles()
  const { update: updateResmgrHostFn } = useUpdateAction(updateResmgrHost)

  // States
  const [isRetrying, setIsRetrying] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local variables
  const showRetryButton = item?.vmhaStatus === VMHAStatusForHosts.FAILED
  const hostOnline = item?.responding === 'online'

  // Handles the retry button click
  const performRetry = useCallback(async () => {
    try {
      setIsRetrying(true)
      setError(null)

      // Apply HA Slave Role
      await updateHostRole({ hostId: item?.id, role: 'pf9-ha-slave', body: {} })

      // Update host list for the given host
      await updateResmgrHostFn({ id: item?.id })

      // Close the confirmation modal if everything went well
      setShowConfirmModal(false)
    } catch (error) {
      const { message } = generateApiErrorPayload(
        error as ApiResponseErrorModel,
        '',
        'Something went wrong while applying HA role. Please try again',
      )
      console.error('Failed to apply HA role:', message)
      setError(message)
    } finally {
      setIsRetrying(false)
    }
  }, [item?.id])

  // Handles the retry button click
  const handleRetryClick = useCallback((e: React.MouseEvent) => {
    // Stop event propagation to prevent any parent click handlers
    e.stopPropagation()
    e.preventDefault()

    setError(null)
    setShowConfirmModal(true)
  }, [])

  // Returns the appropriate icon for the VM HA status
  const getBadgeIcon = useMemo(() => {
    switch (item?.vmhaStatus) {
      case VMHAStatusForHosts.ACTIVE:
        return 'check'
      case VMHAStatusForHosts.FAILED:
        return 'circle-exclamation'
      case VMHAStatusForHosts.CONFIGURING:
        return 'refresh'
      case VMHAStatusForHosts.DISABLED:
      default:
        return 'triangle-exclamation'
    }
  }, [item?.vmhaStatus])

  // Returns the appropriate variant for the VM HA status
  const getBadgeVariant = useMemo(() => {
    switch (item?.vmhaStatus) {
      case VMHAStatusForHosts.ACTIVE:
        return 'success'
      case VMHAStatusForHosts.FAILED:
        return 'error'
      case VMHAStatusForHosts.CONFIGURING:
        return 'warning'
      case VMHAStatusForHosts.DISABLED:
      default:
        return 'warning'
    }
  }, [item?.vmhaStatus])

  return (
    <div className={classes.container}>
      <BadgeWithIcon
        text={item?.vmhaStatus}
        variant={getBadgeVariant}
        icon={getBadgeIcon}
        solid={false}
        spin={item?.vmhaStatus === VMHAStatusForHosts.CONFIGURING}
      />
      <ConfirmationDialog
        open={showConfirmModal}
        loading={isRetrying}
        title="Retry Role Application"
        text="Are you sure you want to retry applying role?"
        cancelText="Cancel"
        confirmText="Confirm"
        onConfirm={performRetry}
        onCancel={() => {
          setError(null)
          setShowConfirmModal(false)
        }}
        error={error ? { title: 'Error', message: error } : undefined}
        stopEventPropagation
      />
      {showRetryButton && (
        <Tooltip
          message={
            hostOnline
              ? 'Retry Role Application'
              : 'You cannot retry role application when the host is offline'
          }
        >
          <div
            onClick={hostOnline ? handleRetryClick : undefined}
            className={clsx(classes.retryIcon, {
              [classes.retryIconDisabled]: !hostOnline,
            })}
          >
            <FontAwesomeIcon size="md">redo</FontAwesomeIcon>
          </div>
        </Tooltip>
      )}
    </div>
  )
}
